<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div class="mb-2 mb-md-0">
            <h1 class="h4 mb-1 font-weight-bold">Customer POs</h1>
            <p class="text-muted mb-0">
              Purchase orders your customers sent you, with the original document
            </p>
          </div>
          <div class="d-flex align-items-center">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              title="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add customer PO
            </CButton>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CCard class="mb-4">
      <CCardBody class="p-4">
        <div v-if="items.length" class="d-flex flex-wrap align-items-center mb-3">
          <CInput
            v-model="search"
            class="mb-0 mr-2 search-input"
            placeholder="Search order number or customer"
            aria-label="Search customer POs"
          >
            <template #prepend-content><CIcon name="cil-magnifying-glass" /></template>
          </CInput>
          <small class="text-muted">
            {{ filteredItems.length }} of {{ items.length }} customer PO{{ items.length === 1 ? "" : "s" }}
          </small>
        </div>

        <!-- Loading (first load) -->
        <div v-if="loading && !items.length" class="text-center py-5">
          <CSpinner color="primary" />
          <p class="mt-3 text-muted mb-0">Loading customer POs…</p>
        </div>

        <!-- Error -->
        <div v-else-if="loadError" class="text-center py-5">
          <CIcon name="cil-warning" size="2xl" class="mb-3 text-danger" />
          <h3 class="h6 font-weight-bold">Couldn't load customer POs</h3>
          <p class="text-muted mb-3">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">
            <CIcon name="cil-reload" /> Try again
          </CButton>
        </div>

        <!-- Empty (first use) -->
        <div v-else-if="!items.length" class="text-center py-5">
          <CIcon name="cil-description" size="2xl" class="mb-3 text-muted" />
          <h3 class="h6 font-weight-bold">No customer POs yet</h3>
          <p class="text-muted mb-3">
            Upload the purchase orders customers send you so they're easy to find later.
          </p>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Add customer PO
          </CButton>
        </div>

        <!-- Empty (filtered) -->
        <div v-else-if="!filteredItems.length" class="text-center py-4">
          <h3 class="h6 font-weight-bold">No customer POs match your search</h3>
          <CButton color="light" class="mt-2" @click="search = ''">
            <CIcon name="cil-x" /> Clear search
          </CButton>
        </div>

        <template v-else>
          <!-- Desktop table -->
          <div class="d-none d-lg-block">
            <CDataTable
              :items="filteredItems"
              :fields="fields"
              :items-per-page="25"
              :items-per-page-select="{ values: [10, 25, 50], label: 'Items per page:' }"
              hover
              sorter
              pagination
              responsive
            >
              <template #orderNo="{ item }">
                <td class="py-2">
                  <router-link :to="`/tenants/SalesOrder/${item.id}`" class="font-weight-bold text-monospace">
                    {{ item.orderNo || "No number" }}
                  </router-link>
                </td>
              </template>
              <template #totalAmount="{ item }">
                <td class="py-2 text-right">RM {{ formatMyr(item.totalAmount) }}</td>
              </template>
              <template #document_link="{ item }">
                <td class="py-2">
                  <a
                    v-if="item.documentId"
                    :href="item.documentUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ item.documentName }}
                  </a>
                  <span v-else class="text-muted">No document</span>
                </td>
              </template>
              <template #show_details="{ item }">
                <td class="py-2 text-right text-nowrap">
                  <CButton
                    color="light"
                    size="sm"
                    class="border mr-1"
                    :aria-label="`Edit customer PO ${item.orderNo || ''}`"
                    title="Edit"
                    @click="onEdit(item)"
                  >
                    <CIcon name="cil-pencil" />
                  </CButton>
                  <CButton
                    color="light"
                    size="sm"
                    class="border text-danger"
                    :aria-label="`Delete customer PO ${item.orderNo || ''}`"
                    title="Delete"
                    @click="showDeleteConfirmation(item)"
                  >
                    <CIcon name="cil-trash" />
                  </CButton>
                </td>
              </template>
            </CDataTable>
          </div>

          <!-- Mobile card list -->
          <div class="d-lg-none">
            <CCard v-for="item in filteredItems" :key="item.id" class="mb-3">
              <CCardBody class="py-3">
                <div class="d-flex justify-content-between align-items-start mb-1">
                  <router-link :to="`/tenants/SalesOrder/${item.id}`" class="font-weight-bold text-monospace">
                    {{ item.orderNo || "No number" }}
                  </router-link>
                  <span class="font-weight-bold text-right">RM {{ formatMyr(item.totalAmount) }}</span>
                </div>
                <div>{{ item.customerName }}</div>
                <div class="small text-muted">{{ item.displayDate }}</div>
              </CCardBody>
              <CCardFooter class="py-2 d-flex justify-content-between bg-transparent">
                <CButton color="light" class="border" @click="onEdit(item)">
                  <CIcon name="cil-pencil" /> Edit
                </CButton>
                <CDropdown color="light" placement="bottom-end" class="border">
                  <template #toggler-content>
                    <CIcon name="cil-options" /> More
                  </template>
                  <CDropdownItem
                    v-if="item.documentId"
                    :href="item.documentUrl"
                    target="_blank"
                  >
                    <CIcon name="cil-file" class="mr-2" /> Open document
                  </CDropdownItem>
                  <CDropdownDivider v-if="item.documentId" />
                  <CDropdownItem class="text-danger" @click="showDeleteConfirmation(item)">
                    <CIcon name="cil-trash" class="mr-2" /> Delete
                  </CDropdownItem>
                </CDropdown>
              </CCardFooter>
            </CCard>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <!-- Delete confirmation -->
    <CModal
      :title="`Delete customer PO ${itemToDelete.orderNo || ''}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
    >
      <p class="mb-0">
        The PO from <strong>{{ itemToDelete.customerName }}</strong>
        (RM {{ formatMyr(itemToDelete.totalAmount) }}) will be removed.
        This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" />
          Delete customer PO
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import SalesOrderApi from "@/lib/salesOrderApi";
import { formatMyr } from "@/utils/money";
import { formatDate } from "@/utils/datetime";

const fields = [
  { key: "orderNo", label: "Order no." },
  { key: "customerName", label: "Customer" },
  { key: "displayDate", label: "Date" },
  { key: "totalAmount", label: "Amount", _style: "text-align: right" },
  { key: "document_link", label: "Document", sorter: false, filter: false },
  { key: "show_details", label: "", _style: "width:1%", sorter: false, filter: false },
];

export default {
  name: "SalesOrderList",
  data() {
    return {
      loading: true,
      loadError: false,
      deleting: false,
      search: "",
      items: [],
      fields,
      api: new SalesOrderApi(),
      warningModal: false,
      itemToDelete: {},
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Sales", "Customer P.O."]);
    this.refreshTable();
  },
  computed: {
    computedItems() {
      return this.items.map((item) => {
        return {
          ...item,
          displayDate: formatDate(item.date),
          documentName: this.getDocumentName(item),
          documentUrl: apiUrl + "documents/file/" + item.documentId,
          customerName: item.customer ? item.customer.name : "No customer",
          totalAmount: Number(item.totalAmount) || 0,
        };
      });
    },
    filteredItems() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.computedItems;
      return this.computedItems.filter(
        (i) =>
          String(i.orderNo || "").toLowerCase().includes(q) ||
          String(i.customerName || "").toLowerCase().includes(q)
      );
    },
  },
  methods: {
    formatMyr,
    getDocumentName(item) {
      if (item.document == null || item.document.fileName == null) return "Open document";
      return item.document.fileName;
    },
    refreshTable() {
      this.loading = true;
      this.loadError = false;
      this.api
        .getListByCurrentBusiness()
        .then((response) => {
          this.items = response.result || [];
        })
        .catch((error) => {
          console.error("Failed to load customer POs", error);
          this.loadError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onEdit(item) {
      this.$router.push({ path: `/tenants/SalesOrder/${item.id}` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    confirmDelete() {
      if (this.deleting) return;
      this.deleting = true;
      const number = this.itemToDelete.orderNo;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.warningModal = false;
          this.itemToDelete = {};
          this.refreshTable();
          this.$toast.success(number ? `Customer PO ${number} deleted` : "Customer PO deleted");
        })
        .catch(({ data } = {}) => {
          this.$toast.error(`Couldn't delete the customer PO. ${helper.getErrorMessage(data)}`);
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    addNew() {
      this.$router.push({ path: "/tenants/SalesOrder" });
    },
  },
};
</script>

<style scoped>
.search-input {
  min-width: 260px;
}

::v-deep .table tbody td {
  vertical-align: middle;
}
</style>
